/**
 * Admin pickup roster — the board an admin coordinates dismissal from.
 *
 * Two renderers over one hook. **Run cards** (the default) put one card per
 * school × grade-band, because that pair is the unit of work: TK & K and G1–G5 dismiss
 * at different times, to different staff. **List** is the same data as one grouped
 * table, which is what you want when the job is to find one child, print, or export.
 */

import { Fragment, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Download,
  LayoutGrid,
  List,
  Printer,
  Search,
} from 'lucide-react';
import {
  useAdminPickupRoster,
  type AttendanceSlot,
  type PickupRosterStudent,
  type WeekDay,
} from '../../hooks/useAdminPickupRoster';
import {
  formatGradeLabel,
  GRADE_BAND_LABEL,
  GRADE_BAND_ORDER,
  type GradeBand,
  type RosterRun,
} from '../../utils/rosterGrouping';
import { SLOT_LABEL, SLOT_SWATCH, type AttendanceSlotStatus } from '../../utils/attendanceSlot';
import { formatDateDisplay } from '../../utils/date';
import { weekdayOfDateString } from '../../utils/schedule';
import { downloadCSV } from '../../utils/csv';
import { AttendanceRecordModal } from '../components/AttendanceRecordModal';

type ViewMode = 'cards' | 'list';
type BandFilter = GradeBand | 'all';

/** Opening the drill-down needs the student it is about and the day that was clicked. */
type SlotSelection = { student: PickupRosterStudent; date: string };
type SelectSlot = (selection: SlotSelection) => void;

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** "Mon 08/17/2026" — the day of the week is what a strip square is looked up by. */
function dayLabel(date: string): string {
  return `${WEEKDAY_SHORT[weekdayOfDateString(date)]} ${formatDateDisplay(date)}`;
}

// ── Attendance strip ─────────────────────────────────────────────────────────

/**
 * This week, Monday to Friday — five squares in fixed calendar positions.
 *
 * Fixed positions are the point: an admin asks "was she here Monday", and a rolling
 * "last five sessions" window made the answer depend on counting backwards from a
 * different day every day. Today's square is outlined so the week can be read at a glance
 * without hovering, and each past square is a button onto the full record for that day —
 * so "who collected them, and when" stays one tap away instead of forcing another column
 * onto a row that already has a name, a room and an allergy badge on it.
 */
function AttendanceStrip({
  student,
  slots,
  today,
  size = 'sm',
  onSelect,
}: {
  student: PickupRosterStudent;
  slots: AttendanceSlot[];
  today: string;
  size?: 'sm' | 'md';
  onSelect: SelectSlot;
}) {
  const box = size === 'md' ? 'w-3 h-3' : 'w-2.5 h-2.5';

  return (
    <span
      className="inline-flex items-center gap-[3px] align-middle"
      aria-label="This week, Monday to Friday"
    >
      {slots.map(slot => {
        const label = `${dayLabel(slot.date)} — ${SLOT_LABEL[slot.status]}`;
        // Today's column, so the strip can be read without hovering every square.
        const marker = slot.date === today ? ' outline outline-1 outline-offset-1 outline-gray-500' : '';
        // A closed day and a day that has not arrived hold nothing to drill into; making
        // them inert keeps every clickable square a promise that something is behind it.
        if (slot.status === 'no_session' || slot.date > today) {
          return (
            <span
              key={slot.date}
              className={`${box} rounded-sm ${SLOT_SWATCH[slot.status]}${marker}`}
              title={label}
            />
          );
        }
        return (
          <button
            key={slot.date}
            type="button"
            onClick={() => onSelect({ student, date: slot.date })}
            title={label}
            aria-label={`${student.displayName}, ${label}. Open details`}
            className={`${box} rounded-sm ${SLOT_SWATCH[slot.status]}${marker} cursor-pointer hover:ring-2 hover:ring-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 print:cursor-auto`}
          />
        );
      })}
    </span>
  );
}

function Legend() {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-500">
      {(Object.keys(SLOT_LABEL) as AttendanceSlotStatus[]).map(status => (
        <span key={status} className="inline-flex items-center gap-1.5">
          <span className={`w-2.5 h-2.5 rounded-sm ${SLOT_SWATCH[status]}`} />
          {SLOT_LABEL[status]}
        </span>
      ))}
      <span className="text-gray-400">Mon → Fri · today is outlined · tap a square for detail</span>
    </div>
  );
}

// ── Shared row bits ──────────────────────────────────────────────────────────

function StudentName({ student }: { student: PickupRosterStudent }) {
  return (
    <span className="inline-flex items-center gap-1.5 min-w-0">
      <span className="truncate">{student.displayName}</span>
      {student.hasAllergies && (
        <span
          className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[10px] font-semibold bg-red-50 text-red-700 flex-shrink-0"
          title={student.allergiesDetails ?? 'Allergies on file'}
        >
          <AlertTriangle className="w-2.5 h-2.5" />
          Allergy
        </span>
      )}
      {student.isFlex && (
        <span className="px-1 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 flex-shrink-0">
          Flex
        </span>
      )}
    </span>
  );
}

function RoomBadge({ room }: { room: string | null }) {
  return room ? (
    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-50 text-blue-700 whitespace-nowrap">
      Rm {room}
    </span>
  ) : (
    <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-400 whitespace-nowrap">
      no room
    </span>
  );
}

/**
 * Live counts for one run, from today's slot.
 *
 * Checked-in and checked-out are counted apart rather than summed into "present": during
 * dismissal the question is who is still on site, and a single total cannot answer it.
 */
function runTally(run: RosterRun<PickupRosterStudent>) {
  let checkedIn = 0;
  let checkedOut = 0;
  let absent = 0;
  let notScheduled = 0;
  for (const grade of run.grades) {
    for (const student of grade.students) {
      const status = student.today?.status;
      if (status === 'checked_in') checkedIn += 1;
      else if (status === 'checked_out') checkedOut += 1;
      else if (status === 'absent') absent += 1;
      else if (status === 'not_scheduled') notScheduled += 1;
    }
  }
  return { checkedIn, checkedOut, absent, notScheduled };
}

// ── Cards view ───────────────────────────────────────────────────────────────

const BAND_ACCENT: Record<GradeBand, string> = {
  tk_k: 'border-t-teal-600',
  g1_5: 'border-t-amber-500',
  other: 'border-t-gray-400',
};

function RunCard({
  run,
  today,
  onSelectSlot,
}: {
  run: RosterRun<PickupRosterStudent>;
  today: string;
  onSelectSlot: SelectSlot;
}) {
  const { checkedIn, checkedOut, absent, notScheduled } = runTally(run);

  return (
    <div
      className={`bg-white border border-gray-200 border-t-[3px] ${BAND_ACCENT[run.band]} rounded-lg flex flex-col overflow-hidden break-inside-avoid`}
    >
      <div className="px-3 py-2 border-b border-gray-100">
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-900 truncate">{run.shortSchool}</h3>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 whitespace-nowrap">
            {run.bandLabel}
          </span>
        </div>
        <p className="text-[11px] text-gray-500 mt-0.5">
          {run.total} student{run.total === 1 ? '' : 's'}
          {run.rooms.length > 0 && ` · rooms ${run.rooms.join(', ')}`}
        </p>
      </div>

      <div className="py-1">
        {run.grades.map(grade =>
          grade.rooms.map((cluster, index) => (
            <div key={`${grade.grade}-${cluster.room ?? 'none'}-${index}`}>
              <div className="flex items-center justify-between gap-2 px-3 pt-1.5 pb-0.5">
                <span className="text-[11px] font-semibold text-gray-600">
                  {formatGradeLabel(grade.grade)}
                </span>
                <RoomBadge room={cluster.room} />
              </div>
              {cluster.students.map(student => (
                <div
                  key={student.studentId}
                  className="flex items-center justify-between gap-2 px-3 py-0.5 text-[13px] text-gray-800"
                >
                  <StudentName student={student} />
                  <AttendanceStrip
                    student={student}
                    slots={student.slots}
                    today={today}
                    onSelect={onSelectSlot}
                  />
                </div>
              ))}
            </div>
          )),
        )}
      </div>

      <div className="mt-auto px-3 py-1.5 border-t border-gray-100 bg-gray-50 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-gray-600 tabular-nums">
        <span className="inline-flex items-center gap-1">
          <span className={`w-1.5 h-1.5 rounded-sm ${SLOT_SWATCH.checked_in}`} />
          {checkedIn} checked in
        </span>
        <span className="inline-flex items-center gap-1">
          <span className={`w-1.5 h-1.5 rounded-sm ${SLOT_SWATCH.checked_out}`} />
          {checkedOut} checked out
        </span>
        {absent > 0 && (
          <span className="inline-flex items-center gap-1">
            <span className={`w-1.5 h-1.5 rounded-sm ${SLOT_SWATCH.absent}`} />
            {absent} absent
          </span>
        )}
        {notScheduled > 0 && (
          <span className="inline-flex items-center gap-1">
            <span className={`w-1.5 h-1.5 rounded-sm ${SLOT_SWATCH.not_scheduled}`} />
            {notScheduled} not scheduled
          </span>
        )}
      </div>
    </div>
  );
}

// ── List view ────────────────────────────────────────────────────────────────

function ListView({
  runs,
  today,
  onSelectSlot,
}: {
  runs: RosterRun<PickupRosterStudent>[];
  today: string;
  onSelectSlot: SelectSlot;
}) {
  return (
    <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg">
      <table className="min-w-full text-[13px]">
        <thead>
          <tr className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500">
            <th className="text-left font-semibold px-3 py-2">Student</th>
            <th className="text-left font-semibold px-3 py-2">Room</th>
            <th className="text-left font-semibold px-3 py-2 hidden md:table-cell">Program</th>
            <th className="text-left font-semibold px-3 py-2">This week (Mon–Fri)</th>
            <th className="text-left font-semibold px-3 py-2 hidden lg:table-cell">Pickup contact</th>
          </tr>
        </thead>
        <tbody>
          {runs.map(run => (
            <Fragment key={run.key}>
              <tr>
                <td colSpan={5} className="px-3 py-1.5 bg-blue-700 text-white font-semibold">
                  {run.shortSchool} · {run.bandLabel} — {run.total} student
                  {run.total === 1 ? '' : 's'}
                </td>
              </tr>
              {run.grades.map(grade => (
                <Fragment key={`${run.key}-${grade.grade}`}>
                  <tr>
                    <td
                      colSpan={5}
                      className="px-3 py-1 pl-6 bg-gray-50 text-[11px] font-semibold text-gray-600"
                    >
                      {formatGradeLabel(grade.grade)} — {grade.students.length}
                    </td>
                  </tr>
                  {grade.students.map(student => {
                    const contact = student.guardians[0];
                    return (
                      <tr key={student.studentId} className="border-b border-gray-100">
                        <td className="px-3 py-1.5 pl-8">
                          <StudentName student={student} />
                        </td>
                        <td className="px-3 py-1.5">
                          <RoomBadge room={student.roomNumber} />
                        </td>
                        <td className="px-3 py-1.5 text-gray-600 hidden md:table-cell">
                          {student.programNames.join(', ') || '—'}
                        </td>
                        <td className="px-3 py-1.5">
                          <AttendanceStrip
                            student={student}
                            slots={student.slots}
                            today={today}
                            size="md"
                            onSelect={onSelectSlot}
                          />
                        </td>
                        <td className="px-3 py-1.5 text-gray-600 hidden lg:table-cell whitespace-nowrap">
                          {contact ? `${contact.name}${contact.phone ? ` · ${contact.phone}` : ''}` : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </Fragment>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── CSV ──────────────────────────────────────────────────────────────────────

function escapeCsv(value: string): string {
  return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

function toCsv(runs: RosterRun<PickupRosterStudent>[], week: WeekDay[]): string {
  const header = [
    'School',
    'Band',
    'Grade',
    'Room',
    'Student',
    'Program',
    'Allergies',
    'Pickup contact',
    'Pickup phone',
    ...week.map(day => dayLabel(day.date)),
  ];
  const rows = runs.flatMap(run =>
    run.grades.flatMap(grade =>
      grade.students.map(student => {
        const contact = student.guardians[0];
        const byDate = new Map(student.slots.map(slot => [slot.date, SLOT_LABEL[slot.status]]));
        return [
          run.school,
          run.bandLabel,
          formatGradeLabel(grade.grade),
          student.roomNumber ?? '',
          student.sortName,
          student.programNames.join('; '),
          student.hasAllergies ? student.allergiesDetails || 'Yes' : '',
          contact?.name ?? '',
          contact?.phone ?? '',
          ...week.map(day => byDate.get(day.date) ?? 'No session'),
        ];
      }),
    ),
  );
  return [header, ...rows].map(row => row.map(cell => escapeCsv(String(cell))).join(',')).join('\n');
}

// ── Page ─────────────────────────────────────────────────────────────────────

/**
 * ‹ This week › — steps the board forward to see who is expected in a coming week. Never
 * back past the current week: the attendance history tab is the archive, and this board's
 * enrollment rule is about who is on the line from now on.
 */
function WeekNav({
  weekOffset,
  weekStart,
  onChange,
}: {
  weekOffset: number;
  weekStart: string | null;
  onChange: (offset: number) => void;
}) {
  const button =
    'inline-flex items-center justify-center w-7 h-7 rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500';
  return (
    <div className="inline-flex items-center gap-1" data-testid="week-nav">
      <button
        type="button"
        className={button}
        onClick={() => onChange(weekOffset - 1)}
        disabled={weekOffset === 0}
        aria-label="Previous week"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>
      <span className="min-w-[7.5rem] text-center text-xs font-medium text-gray-700">
        {weekOffset === 0
          ? 'This week'
          : weekOffset === 1
            ? 'Next week'
            : weekStart
              ? `Week of ${formatDateDisplay(weekStart)}`
              : `In ${weekOffset} weeks`}
      </span>
      <button
        type="button"
        className={button}
        onClick={() => onChange(weekOffset + 1)}
        aria-label="Next week"
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}

export function AdminPickupRoster() {
  const [weekOffset, setWeekOffset] = useState(0);
  const { data, isLoading, error, isPlaceholderData } = useAdminPickupRoster(weekOffset);
  const [view, setView] = useState<ViewMode>('cards');
  const [band, setBand] = useState<BandFilter>('all');
  const [search, setSearch] = useState('');
  const [selectedSlot, setSelectedSlot] = useState<SlotSelection | null>(null);

  const bandsPresent = useMemo(() => {
    const present = new Set((data?.runs ?? []).map(run => run.band));
    return GRADE_BAND_ORDER.filter(b => present.has(b));
  }, [data?.runs]);

  const runs = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (data?.runs ?? [])
      .filter(run => band === 'all' || run.band === band)
      .map(run => {
        if (!term) return run;
        // Filtering rebuilds the run so its counts and tallies describe what is on
        // screen rather than what was there before the search.
        const grades = run.grades
          .map(grade => {
            const rooms = grade.rooms
              .map(cluster => ({
                ...cluster,
                students: cluster.students.filter(s =>
                  `${s.displayName} ${s.sortName}`.toLowerCase().includes(term),
                ),
              }))
              .filter(cluster => cluster.students.length > 0);
            return { ...grade, rooms, students: rooms.flatMap(r => r.students) };
          })
          .filter(grade => grade.students.length > 0);
        return {
          ...run,
          grades,
          total: grades.reduce((sum, grade) => sum + grade.students.length, 0),
        };
      })
      .filter(run => run.total > 0);
  }, [data?.runs, band, search]);

  const shownCount = runs.reduce((sum, run) => sum + run.total, 0);

  if (isLoading) {
    return <div className="p-8 text-center text-gray-500">Loading roster…</div>;
  }

  if (error) {
    return (
      <div className="m-4 p-4 bg-red-50 text-red-700 rounded">
        Could not load the pickup roster: {error instanceof Error ? error.message : 'unknown error'}
      </div>
    );
  }

  const weekNav = (
    <WeekNav weekOffset={weekOffset} weekStart={data?.week[0]?.date ?? null} onChange={setWeekOffset} />
  );

  if (!data || data.studentCount === 0) {
    // A coming week can be empty (a closure week, the summer) — keep the controls, or
    // there is no way back.
    if (weekOffset > 0) {
      return (
        <div className="flex flex-col gap-3 p-4">
          <div className="print:hidden">{weekNav}</div>
          <div className="p-8 text-center text-gray-500">
            Nobody is enrolled in after-school that week.
          </div>
        </div>
      );
    }
    return (
      <div className="p-8 text-center text-gray-500">
        No students are currently enrolled in an after-school program.
      </div>
    );
  }

  const week = data.week;
  const sessionsThisWeek = week.filter(day => day.isSession).length;
  // An empty week is either the run-up to the first day of school or a closure week, and
  // the two want different sentences — "starts on the 11th" is nonsense in December.
  const beforeFirstSession =
    data.programStartDate !== null && data.programStartDate > week[week.length - 1].date;

  return (
    <div className={`flex flex-col gap-3 p-4${isPlaceholderData ? ' opacity-60' : ''}`}>
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2 print:hidden">
        {weekNav}

        <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
          <button
            type="button"
            onClick={() => setView('cards')}
            aria-pressed={view === 'cards'}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 ${
              view === 'cards' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <LayoutGrid className="w-3.5 h-3.5" /> Runs
          </button>
          <button
            type="button"
            onClick={() => setView('list')}
            aria-pressed={view === 'list'}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border-l border-gray-300 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 ${
              view === 'list' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <List className="w-3.5 h-3.5" /> List
          </button>
        </div>

        {bandsPresent.length > 1 && (
          <div className="inline-flex gap-1">
            {(['all', ...bandsPresent] as BandFilter[]).map(option => (
              <button
                key={option}
                type="button"
                onClick={() => setBand(option)}
                aria-pressed={band === option}
                className={`px-2.5 py-1 rounded-full text-xs border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  band === option
                    ? 'bg-gray-900 border-gray-900 text-white font-semibold'
                    : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {option === 'all' ? 'All runs' : GRADE_BAND_LABEL[option]}
              </button>
            ))}
          </div>
        )}

        <div className="relative">
          <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Find a student"
            aria-label="Find a student"
            className="pl-8 pr-3 py-1.5 text-xs border border-gray-300 rounded-md w-44 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="flex-1" />

        <button
          type="button"
          onClick={() =>
            downloadCSV(
              toCsv(runs, week),
              `pickup-roster-${weekOffset === 0 ? data.today : `week-of-${week[0].date}`}.csv`,
            )
          }
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <Download className="w-3.5 h-3.5" /> CSV
        </button>
        <button
          type="button"
          onClick={() => window.print()}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <Printer className="w-3.5 h-3.5" /> Print
        </button>
      </div>

      {/* Status line */}
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
        <span className="tabular-nums">
          {formatDateDisplay(data.today)} · week of {formatDateDisplay(week[0].date)} ·{' '}
          {shownCount} of {data.studentCount} students · {runs.length} run
          {runs.length === 1 ? '' : 's'} · {data.schoolCount} schools
        </span>
        <Legend />
      </div>

      {weekOffset > 0 && (
        <div className="px-3 py-2 text-xs bg-sky-50 text-sky-800 rounded border border-sky-200">
          A coming week: this is who is expected, by each child's own enrollment dates and Flex
          plan. Nothing has been recorded yet apart from absences reported in advance. A Flex
          child with no plan on file for that month shows as upcoming, not as not scheduled.
        </div>
      )}

      {(data.isUpcoming || sessionsThisWeek === 0) && (
        <div className="px-3 py-2 text-xs bg-amber-50 text-amber-800 rounded border border-amber-200">
          {data.isUpcoming ? (
            <>
              The term has not started yet — this is the <strong>upcoming</strong> roster
              {data.termStartDate && ` for ${formatDateDisplay(data.termStartDate)}`}. Attendance
              squares stay empty until the first session.
            </>
          ) : beforeFirstSession ? (
            <>
              No sessions have run yet
              {data.programStartDate &&
                ` — after-school starts ${formatDateDisplay(data.programStartDate)}`}
              . Attendance squares stay empty until then.
            </>
          ) : (
            <>
              No sessions {weekOffset > 0 ? 'that' : 'this'} week — after-school is closed Monday
              through Friday. The squares
              below are the whole week, so all five read as no session.
            </>
          )}
        </div>
      )}

      {runs.length === 0 ? (
        <div className="p-8 text-center text-gray-500 text-sm">No students match that search.</div>
      ) : view === 'cards' ? (
        <div className="grid gap-3 grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 items-start">
          {runs.map(run => (
            <RunCard key={run.key} run={run} today={data.today} onSelectSlot={setSelectedSlot} />
          ))}
        </div>
      ) : (
        <ListView runs={runs} today={data.today} onSelectSlot={setSelectedSlot} />
      )}

      {selectedSlot && (
        <AttendanceRecordModal
          studentId={selectedSlot.student.studentId}
          studentName={selectedSlot.student.displayName}
          photoPath={selectedSlot.student.photoPath}
          focusDate={selectedSlot.date}
          onClose={() => setSelectedSlot(null)}
        />
      )}
    </div>
  );
}
